import { yupResolver } from "@hookform/resolvers/yup";
import {
    Box,
    Divider,
    Grid
} from "@mui/material";
import { Container } from "@mui/system";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormInputDate from "../../components/form/FormInputDate";
import { useInstitutionFlags } from "../../components/hooks/useInstitutionFlags";
import {
    AllK10Stats, ClientsNumberStats, DateRange, NoEventStats, PmStats,
    ScreeningStats
} from "../../data/models/stats";
import { useStore } from "../../stores/store";
import { RoundButton } from "../../_styles/StyledButtons";
import { statsFormSchema } from "../../_validators/schemas/statsForm.schema";
import DeactivatedPMsStatsTable from "./DeactivatedPMsStatsTable";
import DemographicTable from "./DemographicTable";
import GenderSpecificNumberOfClientsTable from "./GenderSpecificNumberOfClientsTable";
import K10Table from "./K10Table";
import LanguageTable from "./LanguageTable";
import NoShowPMsTable from "./NoShowPMsTable";
import NumberOfClientsStats from "./NumberOfClientsStats";
import PMTable from "./PMTable";
import "./styles.css";

function StatsTable() {
    const { t } = useTranslation();
    const { institutionStore, dataExportStore, loadingStore } = useStore();
    const { selectedUserInstitution } = institutionStore;
    const {
        getSessionStats,
        getInterventionStats,
        getNoEventUsers,
        getCompletedPMEvents,
        getK10Stats,
        getNumberOfClients,
        getGenderSpecificNumberOfClients,
        getNoShowInterventions,
        getDeactivatedInterventions
    } = dataExportStore;
    const { isLoading } = loadingStore;
    const { control, handleSubmit, setValue } = useForm({
        resolver: yupResolver(statsFormSchema()),
    });

    const [sessionData, setSessionData] = useState<ScreeningStats | null>(null);
    const [interventionData, setinterventionData] =
        useState<ScreeningStats | null>(null);
    const [numberOfClients, setNumberOfClients] = useState<ClientsNumberStats | null>(null);
    const [genderSpecificNumberOfClients, setGenderSpecificNumberOfClients] = useState<NoEventStats[]>([]);
    const [noEventStats, setNoEventStats] = useState<NoEventStats[]>([]);
    const [pmStats, setPmStats] = useState<PmStats[]>([]);
    const [noShowPmStats, setNoShowPmStats] = useState<PmStats[]>([]);
    const [deactivatedPmStats, setDeactivatedPmStats] = useState<PmStats[]>([]);
    const [k10Stats, setK10Stats] = useState<AllK10Stats>({
        bl: null,
        t2: null,
        t3: null,
        t4: null,
        t5: null,
    });

    const { isSpirit, isBright } = useInstitutionFlags();

    let date = new Date();
    const [firstDay, setFirstDay] = useState(
        dayjs(new Date(date.getFullYear(), date.getMonth(), 1))
    );
    const [lastDay, setLastDay] = useState(
        dayjs(new Date(date.getFullYear(), date.getMonth() + 1, 0))
    );

    useEffect(() => {
        setValue("startDate", firstDay.toDate());
        setValue("endDate", lastDay.toDate());
        const fetchData = async () => {
            const now = new Date();
            now.setMonth(now.getMonth() + 1);

            const range: DateRange = {
                startDate: firstDay.toISOString(),
                endDate: lastDay.toISOString(),
            };
            onSubmit(range);
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUserInstitution]);

    const onSubmit = async (data: any) => {
        const startDate = dayjs(data.startDate).add(dayjs(data.startDate).utcOffset(), 'minute').toISOString();
        const endDate = dayjs(data.endDate).add(dayjs(data.endDate).utcOffset(), 'minute')
            .add(23, 'hours')
            .add(59, 'minutes')
            .add(59, 'seconds')
            .toISOString();

        setFirstDay(dayjs(data.startDate));
        setLastDay(dayjs(data.endDate));

        const range: DateRange = {
            startDate,
            endDate,
        };

        if (selectedUserInstitution) {
            const institutionId = selectedUserInstitution.institutionId;
            const sessionStats = await getSessionStats(institutionId, range);
            const interventionStats = await getInterventionStats(
                institutionId,
                range
            );

            const numberOfClients = await getNumberOfClients(institutionId, range);
            const genderSpecificNumberOfClients = await getGenderSpecificNumberOfClients(institutionId, range);
            const noEventStats = await getNoEventUsers(institutionId, range);
            const pmStats = await getCompletedPMEvents(institutionId, range);
            const noShowPmStats = await getNoShowInterventions(institutionId, range);
            const deactivatedPmStats = await getDeactivatedInterventions(institutionId, range);
            const bl = await getK10Stats(institutionId, 0, range);
            const t2 = await getK10Stats(institutionId, 1, range);
            const t3 = await getK10Stats(institutionId, 2, range);
            const t4 = await getK10Stats(institutionId, 3, range);
            const t5 = await getK10Stats(institutionId, 4, range);

            setK10Stats({
                bl,
                t2,
                t3,
                t4,
                t5,
            });

            sessionStats.demographicStats.forEach(stat => {
                stat.location = stat.location?.trim();
            });

            sessionStats.languageStats.forEach(stat => {
                stat.location = stat.location?.trim();
            });
            setSessionData(sessionStats);
            setNumberOfClients(numberOfClients);
            setGenderSpecificNumberOfClients(genderSpecificNumberOfClients);
            setinterventionData(interventionStats);
            setNoEventStats(noEventStats);
            setPmStats(pmStats);
            setNoShowPmStats(noShowPmStats);
            setDeactivatedPmStats(deactivatedPmStats);


            setK10Stats({
                bl,
                t2,
                t3,
                t4,
                t5,
            });
        }
    };

    if (!sessionData || !interventionData) return <div>Loading...</div>;

    return (
        <Container maxWidth="xl" className="stats-container">
            <Grid
                container
                component={"form"}
                sx={{ display: "flex", alignItems: "center", maxWidth: "500px" }}
                onSubmit={handleSubmit(onSubmit)}
            >
                <Grid item xs={4} mr={2}>
                    <FormInputDate
                        name="startDate"
                        control={control}
                        date={firstDay.toDate()}
                        label={`${t("EVENT_START_DATE")}`}
                    />
                </Grid>
                <Grid item xs={4} mr={2}>
                    <FormInputDate
                        name="endDate"
                        control={control}
                        date={lastDay.toDate()}
                        label={`${t("EVENT_END_DATE")}`}
                    />
                </Grid>
                <Grid item xs={2}>
                    <RoundButton
                        variant="text"
                        type="submit"
                        disabled={isLoading([
                            getCompletedPMEvents,
                            getInterventionStats,
                            getK10Stats,
                            getSessionStats,
                            getNoEventUsers,
                            getNumberOfClients,
                            getGenderSpecificNumberOfClients,
                            getNoShowInterventions,
                            getDeactivatedInterventions
                        ])}
                    >
                        {t("APPLY")}
                    </RoundButton>
                </Grid>
            </Grid>

            <Box width={"100%"}>
                <NumberOfClientsStats numberOfClients={numberOfClients?.numberOfClients} title={t("REGISTERED_CLIENTS_STAT_TITLE")} />
            </Box>
            <Box width={"100%"} mt={"2rem"}>
                <GenderSpecificNumberOfClientsTable data={genderSpecificNumberOfClients} />
            </Box>
            <Divider
                sx={{ borderBottomWidth: "medium", mt: "2rem", width: "100%" }}
            />

            <h1>{t("DASHBOARD_SCREENING_HEADER")}</h1>
            <Box width={"100%"} mt={"2rem"}>
                <DemographicTable
                    data={sessionData.demographicStats}
                    title={t("STAT_T1_TITLE")}
                    timeline="SCREENING"

                />
                <LanguageTable data={sessionData.languageStats} title={t("STAT_T1_LANG_TITLE")} timeline="SCREENING" />
            </Box>
            <Divider
                sx={{ borderBottomWidth: "medium", mt: "2rem", width: "100%" }}
            />

            <h1>{t("DASHBOARD_INTERVENTION_HEADER")}</h1>
            <Box width={"100%"} mt={"2rem"}>
                <DemographicTable
                    data={interventionData.demographicStats}
                    title={t("STAT_T1_TITLE")}
                    timeline="PM+"
                />
                <LanguageTable
                    data={interventionData.languageStats}
                    title={t("STAT_T1_LANG_TITLE")}
                    timeline="PM+"
                />
            </Box>
            {/* <Divider
                sx={{ borderBottomWidth: "medium", mt: "2rem", width: "100%" }}
            />
            <Box width={"100%"} mt={"2rem"}>
                <NoEventTable data={noEventStats} title={t("STAT_T3_TITLE")} />
            </Box> */}
            {/* <Divider
                sx={{ borderBottomWidth: "medium", mt: "2rem", width: "100%" }}
            /> */}
            <Box width={"100%"} mt={"2rem"}>
                <DeactivatedPMsStatsTable data={deactivatedPmStats} title={t("WITHDRAWAL_FROM_PM_TITLE")} />
            </Box>

            <Box width={"100%"} mt={"2rem"}>
                <PMTable data={pmStats} title={t("STAT_T4_TITLE")} />
            </Box>

            <Box width={"100%"} mt={"2rem"}>
                <NoShowPMsTable data={noShowPmStats} title={t("NO_SHOW_PM_TITLE")} />
            </Box>
            <Divider
                sx={{ borderBottomWidth: "medium", mt: "2rem", width: "100%" }}
            />
            <h1>{t("DASHBOARD_TRIAGE_HEADER")}</h1>
            <Box width={"100%"} mt={"2rem"}>
                <K10Table data={k10Stats.bl} title={t("5A")} />
            </Box>
            {
                isSpirit &&
                <Box width={"100%"} mt={"2rem"}>
                    <K10Table data={k10Stats.t2} title={t("5B")} />
                </Box>
            }
            <Box width={"100%"} mt={"2rem"}>
                <K10Table data={k10Stats.t3} title={t("5C")} />
            </Box>
            {
                isBright &&
                <>
                    <Box width={"100%"} mt={"2rem"}>
                        <K10Table data={k10Stats.t4} title={t("5D")} />
                    </Box>
                    <Box width={"100%"} mt={"2rem"}>
                        <K10Table data={k10Stats.t5} title={t("5E")} />
                    </Box>
                </>
            }
        </Container>
    );
}

export default observer(StatsTable);
